import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { SelectButton } from "primereact/selectbutton";
import { Message } from "primereact/message";

import { ConfirmDialog, Dialog, FormField } from "@/ds";

import { JanelaBloco } from "../janela-bloco";
import {
  JANELA_HINT,
  JANELA_QUESTION,
  OBRIGATORIO_OPTIONS,
  OBRIGATORIO_QUESTION,
  PAPEL_DISCARD_ACCEPT_LABEL,
  PAPEL_DISCARD_MESSAGE,
  PAPEL_DISCARD_REJECT_LABEL,
  PAPEL_DISCARD_TITLE,
  PAPEL_NAME_LABEL,
  PAPEL_NAME_MAX_LENGTH,
} from "./papel-obra-form-dialog.constants";
import styles from "./papel-obra-form-dialog.module.scss";
import type { PapelObraFormDialogViewModel } from "./types-papel-obra-form-dialog";

export function PapelObraFormDialogView({
  titulo,
  formId,
  nome,
  obrigatorio,
  mostrarJanela,
  entrada,
  saida,
  janelaError,
  isSubmitting,
  salvarDesabilitado,
  enviar,
  cancelar,
  descarte,
}: PapelObraFormDialogViewModel) {
  return (
    <>
      <Dialog
        visible
        // O Prime acharia o primeiro botão do rodapé; o foco inicial é o `autoFocus` do campo.
        focusOnShow={false}
        size="lg"
        header={titulo}
        onHide={cancelar}
        locked={isSubmitting}
        footer={
          <div className={styles.footer}>
            <Button
              type="button"
              label="Cancelar"
              severity="secondary"
              text
              disabled={isSubmitting}
              onClick={cancelar}
              data-testid="papel-obra-cancel"
            />
            <Button
              type="submit"
              form={formId}
              label="Salvar"
              loading={isSubmitting}
              disabled={salvarDesabilitado}
              data-testid="papel-obra-save"
            />
          </div>
        }
        data-testid="papel-obra-form-dialog"
      >
        <form id={formId} className={styles.form} onSubmit={enviar} noValidate>
          <FormField.Root error={nome.error}>
            <FormField.Label>{PAPEL_NAME_LABEL}</FormField.Label>
            <FormField.Control>
              <InputText
                autoFocus={!nome.readOnly}
                value={nome.value}
                maxLength={PAPEL_NAME_MAX_LENGTH}
                readOnly={nome.readOnly}
                aria-readonly={nome.readOnly}
                disabled={isSubmitting}
                title={nome.hint}
                onChange={(event) => nome.digitar(event.currentTarget.value)}
                onBlur={nome.sairDo}
                data-testid="papel-obra-nome"
              />
            </FormField.Control>
            <FormField.Error data-testid="papel-obra-nome-error" />
          </FormField.Root>

          <section className={styles.pergunta} data-testid="papel-obra-obrigatorio-box">
            <div className={styles.titulo}>
              <span className={styles.numero}>1</span>
              <span className={styles.texto}>{OBRIGATORIO_QUESTION}</span>
            </div>

            <SelectButton
              className={styles.escolha}
              value={obrigatorio.value}
              options={OBRIGATORIO_OPTIONS}
              optionLabel="label"
              optionValue="value"
              allowEmpty={false}
              disabled={isSubmitting}
              onChange={(event) => obrigatorio.escolher(event.value)}
              aria-label={OBRIGATORIO_QUESTION}
              data-testid="papel-obra-obrigatorio"
            />
          </section>

          {mostrarJanela ? (
            <section className={styles.pergunta} data-testid="papel-obra-janela-box">
              <div className={styles.titulo}>
                <span className={styles.numero}>2</span>
                <span className={styles.texto}>{JANELA_QUESTION}</span>
              </div>

              <p className={styles.dica}>{JANELA_HINT}</p>

              <JanelaBloco {...entrada} />
              <JanelaBloco {...saida} />

              {janelaError ? (
                <Message
                  severity="error"
                  text={janelaError}
                  data-testid="papel-obra-janela-error"
                />
              ) : null}
            </section>
          ) : null}
        </form>
      </Dialog>

      {descarte.aberto ? (
        <ConfirmDialog
          visible
          variant="destructive"
          header={PAPEL_DISCARD_TITLE}
          message={PAPEL_DISCARD_MESSAGE}
          acceptLabel={PAPEL_DISCARD_ACCEPT_LABEL}
          rejectLabel={PAPEL_DISCARD_REJECT_LABEL}
          accept={descarte.confirmar}
          reject={descarte.continuar}
          onHide={descarte.continuar}
          testId="papel-obra-descarte"
        />
      ) : null}
    </>
  );
}
